import React, { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import MdaeButton from "../common_widgets/button";
import MdaePopup, {
    MdaePopupTitle,
    MdaePopupContent,
    MdaePopupFooter,
    MdaePopupFooterEndButtons,
    MdaePopupFooterStartButtons,
  } from "../../common_components/common_widgets/popup";
  import MdaeDataGrid, {
    IMdaeDataGridProps,
  } from "../../common_components/common_widgets/data-grid";
import { addSelectedCasesToAudit, SET_CASE_LIST_GRID_SELECTED_ROW_KEYS, TOGGLE_CASE_SEARCH_LIST_POPUP} from "./reducers/case-search-reducer";
import { first, isEqual } from "lodash";
import "./index.scss";
import CopyText from "../common_widgets/copy-text";
import CaseDetail, { CASE_DETAIL_POPUP_MODES } from "../../pages/external-audit/case-detail/component";
import Guid from "devextreme/core/guid";
import { caseSearchTypes } from "./constants";
import { toggleCaseDetailPopup } from "../../pages/external-audit/case-detail/case-detail-common-functions";
import { linkedAuditColumnCellRender, viewCaseDetails } from "./case-search-list-popup";
import MdaeRadioGroup from "../common_widgets/radio-group";

export const toggleCaseSearchListPopupVisible = (
  dispatch,
  isVisible,
  caseSearchType?
) => {
  dispatch({
    type: TOGGLE_CASE_SEARCH_LIST_POPUP,
    data: {
      isCaseSearchListPopupVisible: isVisible,
      caseSearchType: caseSearchType
    },
  });
};
export const CASE_SEARCH_MODES = {
  SINGLE_CASE: "SINGLE_CASE",
  MULTIPLE_CASE: "MULTIPLE_CASE"
}
interface ICaseSearchListPopup {
  mode?: string;
  showClaimTypeSelector?: boolean;
}

const CaseSearchListPopup: React.FC<ICaseSearchListPopup> = (props) => {
  const caseSearchListMode =
    props.mode ?? CASE_SEARCH_MODES.MULTIPLE_CASE;
  const dispatch = useDispatch();
  const auditPropertiesData = useSelector(
    (state) => state["auditPropertiesReducer"].auditPropertiesData,
    isEqual
  );

  const caseFoundList = useSelector(
    (state) => state["caseSearchReducer"].caseFoundList,
    isEqual
  );

  const caseNotFoundList = useSelector(
    (state) => state["caseSearchReducer"].caseNotFoundList,
    isEqual
  );

  const caseListGridSelectedRowKeys = useSelector(
    (state) => state["caseSearchReducer"].caseListGridSelectedRowKeys,
    isEqual
  );

  const eaCaseListData = useSelector(
    (state) => state["caseListReducer"].eaCaseListData,
    isEqual
  );

  const externalAuditSelectedIndex = useSelector(
    (state) => state["externalAuditReducer"].externalAuditSelectedIndex,
    isEqual
  );

  const caseSearchType = useSelector(
    (state) => state["caseSearchReducer"].caseSearchType,
    isEqual
  );

  const keyInformationInvalidCaseSearchData = useSelector(
    (state) =>
      state["caseSearchReducer"].keyInformationCaseSearchData
        ?.keyInformationInvalidCaseSearchData,
    isEqual
  );

  const isCaseSearchListPopupVisible = useSelector(
    (state) => state["caseSearchReducer"].isCaseSearchListPopupVisible,
    isEqual
  );
  const claimTypes = useSelector(
    (state) => state["auditPropertiesReducer"].claimTypes
  );

  const claimTypeCd = useSelector(
    (state) => state["caseSearchPageReducer"].claimTypeCd
  );

  const [
    caseSearchListDataGridInstance,
    setCaseSearchListDataGridInstance,
  ] = useState(null);

  const onCaseSearchListDataGridInitialized = (e) => {
    setCaseSearchListDataGridInstance(e.component);
  };

  const getCaseListPopupTitle = () => {
    return (
      <MdaePopupTitle
        closePopupHandler={() => {
          toggleCaseSearchListPopupVisible(dispatch, false);
        }}
      >
        Case List
      </MdaePopupTitle>
    );
  };

  const caseSearchListDataGridOptions = useMemo(() => {
    return getCaseSearchListDataGridOptions(
      onCaseSearchListDataGridInitialized,
      caseFoundList,
      dispatch,
      props.mode
    );
  }, [caseFoundList]);

  const getSelectedRowsData = () => {
    return caseSearchListDataGridInstance.getSelectedRowsData();
  };

  const onCaseSearchListAddButtonClick = useCallback(() => {
    if (caseSearchListMode === CASE_SEARCH_MODES.SINGLE_CASE) {
      toggleCaseSearchListPopupVisible(dispatch, false);
      toggleCaseDetailPopup(
        dispatch,
        true,
        "Add Case",
        {
          ...first(getSelectedRowsData()),
          mode: CASE_DETAIL_POPUP_MODES.CREATE,
          claimType: claimTypes?.find((x) => x.code === claimTypeCd)
            ?.description,
        },
        0
      );
    } else {
      dispatch(addSelectedCasesToAudit(getSelectedRowsData(), null));
    }
  }, [
    dispatch,
    getSelectedRowsData,
    claimTypes,
    claimTypeCd,
    addSelectedCasesToAudit,
  ]);

  return (
    <MdaePopup
      deferRendering={false}
      height={620}
      width={1360}
      mdaeTitleRender={getCaseListPopupTitle}
      onShown={(e) => {
        if (caseFoundList.length > 0) {
          let caseListGridRowKeys = caseFoundList.map((item) => item.caseDetailId);
          dispatch({
            type: SET_CASE_LIST_GRID_SELECTED_ROW_KEYS,
            data: {
              caseListGridSelectedRowKeys: caseListGridRowKeys,
            },
          });
        }
      }}
      mdaeDataUpdaterConfig={[
        {
          actionType: TOGGLE_CASE_SEARCH_LIST_POPUP,
          reducerName: "caseSearchReducer",
          widgetOptionBindings: {
            visible: "isCaseSearchListPopupVisible",
            mdaePopupTitle: "Case List",
          },
        },
      ]}
      mdaeContentRender={() => {
        return (
          <div>
            <MdaePopupContent className="case-search-list-popup-content">
              <MdaeDataGrid {...caseSearchListDataGridOptions}></MdaeDataGrid>
              <div
                className={
                  "widget-separation-margin-top case-list-summary-conatiner"
                }
              >
                {caseSearchType == caseSearchTypes.EA_ACCOUNT_CASE_SEARCH && (
                  <div
                    className={
                      "mdae-data-field-content non-matching-case-info-conatiner"
                    }
                  >
                    {caseNotFoundList.length > 0 && (
                      <div>
                        Matching cases for {caseNotFoundList.length} accounts
                        were not found. You will need to search for these cases
                        using a different method or manually enter the case(s).
                        <br />
                        {caseNotFoundList
                          .map((item) => item.patientAcctNo)
                          .join(", ")}
                        <CopyText
                          textToCopy={caseNotFoundList
                            .map((item) => item.patientAcctNo)
                            .join(", ")}
                        ></CopyText>
                      </div>
                    )}
                  </div>
                )}
                {caseSearchType ==
                  caseSearchTypes.EA_KEY_INFORMATION_CASE_SEARCH && (
                  <div
                    className={
                      "mdae-data-field-content non-matching-case-info-conatiner"
                    }
                  >
                    {keyInformationInvalidCaseSearchData.length > 0 && (
                      <div>
                        The following rows did not meet the minimum data
                        requirements of 3 fields- 1 patient identifier and 1
                        service identifier:
                        <br />
                        {keyInformationInvalidCaseSearchData
                          .map((item) => item.rowNumber)
                          .join(", ")}
                      </div>
                    )}
                    {caseNotFoundList.length > 0 && (
                      <div>
                        <br />
                        Matching cases for {caseNotFoundList.length} entries
                        were not found. You will need to search for these cases
                        using a different method or manually enter the case(s).
                        The following rows contained the unmatched data:
                        <br />
                        {caseNotFoundList
                          .map((item) => item.rowNumber)
                          .join(", ")}
                      </div>
                    )}
                  </div>
                )}
                <div
                  className={
                    "mdae-data-field-label application-section-primary-color total-cases-selected-container"
                  }
                >
                  Total Cases Selected : {caseListGridSelectedRowKeys.length}
                </div>
              </div>
            </MdaePopupContent>
            {isCaseSearchListPopupVisible && (
              <CaseDetail
                auditGroupId={auditPropertiesData.auditGroupId}
                eaRoundId={0}
                caseMyTaskWidgetId={new Guid().toString()}
                caseMyPastDueTaskDrawerId={new Guid().toString()}
                caseMyThisWeekTaskDrawerId={new Guid().toString()}
                caseMyUpcomingTaskDrawerId={new Guid().toString()}
                caseMyCompletedTaskDrawerId={new Guid().toString()}
                isViewOnly={true}
              ></CaseDetail>
            )}
          </div>
        );
      }}
      mdaeFooterRender={() => {
        return (
          <MdaePopupFooter>
            <MdaePopupFooterStartButtons>
              <MdaeButton
                id={"pnlCancelCaseSearchListSecondaryBtn"}
                role={"secondary"}
                onClick={() => {
                  toggleCaseSearchListPopupVisible(dispatch, false);
                }}
              >
                Cancel
              </MdaeButton>
            </MdaePopupFooterStartButtons>
            <MdaePopupFooterEndButtons>
              <MdaeButton
                id={"pnlAddSelectedCasesToAuditPrimaryBtn"}
                role={"primary"}
                onClick={onCaseSearchListAddButtonClick}
              >
                Add Selected Cases to Audit
              </MdaeButton>
            </MdaePopupFooterEndButtons>
          </MdaePopupFooter>
        );
      }}
    ></MdaePopup>
  );
};



const getCaseSearchListDataGridOptions = (
  onCaseSearchListDataGridInitialized,
  caseFoundList: any[],
  dispatch,
  caseSearchListMode = CASE_SEARCH_MODES.MULTIPLE_CASE
) => {
  const caseSearchListDataGridOptions: IMdaeDataGridProps = {
    id: "grdCaseSearchList",
    allowColumnResizing: true,
    onInitialized: onCaseSearchListDataGridInitialized,
    export: { enabled: true },
    filterRow: { visible: true },
    headerFilter: { visible: true },
    scrolling: {
      columnRenderingMode: "virtual",
    },
    selection: {
      mode:
        caseSearchListMode === CASE_SEARCH_MODES.MULTIPLE_CASE
          ? "multiple"
          : "single",
      showCheckBoxesMode: "always",
      selectAllMode: "allPages",
    },
    paging: {
      pageSize: 20,
    },
    pager: {
      displayMode: "full",
      showPageSizeSelector: true,
      allowedPageSizes: [10, 20, 30],
      showInfo: true,
    },
    dataSource: caseFoundList,
    height: (window.innerHeight * 2) / 3,
    keyExpr: "caseDetailId",
    mdaeColumns: [
      {
        dataField: "caseId",
        caption: "Case ID",
        dataType: "string",
        allowSorting: true,
        allowFiltering: true,
      },
      {
        name: "isLinkedAuditPresent",
        dataField: "isLinkedAuditPresent",
        caption: "",
        alignment: "center",
        allowFiltering: false,
        allowExporting: false,
        cellRender: (celData) => linkedAuditColumnCellRender(dispatch, celData),
        width: 40,
      },
      {
        name: "actionId",
        dataField: "caseId",
        dataType: "string",
        caption: "",
        alignment: "center",
        allowFiltering: false,
        allowExporting: false,
        cellRender: (cellData) => {
          return (
            <div className={"case-list-preview-action"}>
              <i
                style={{ height: "14px" }}
                className={"preview-action-icon"}
                onClick={(e) => {
                  viewCaseDetails(dispatch, cellData.data);
                }}
              ></i>
            </div>
          );
        },
        width: 40,
      },
      {
        dataField: "subscriberId",
        caption: "Subscriber ID",
        allowFiltering: true,
        allowSorting: true,
        allowHeaderFiltering: false,
      },
      {
        dataField: "patientAcctNoOrig",
        caption: "Pt Acct No",
        allowFiltering: true,
        allowSorting: true,
        allowHeaderFiltering: false,
      },
      {
        dataField: "patientMRN",
        caption: "MRN",
        allowFiltering: true,
        allowSorting: true,
        allowHeaderFiltering: false,
      },
      {
        dataField: "statementFromDate",
        caption: "Service From Date",
        width: 120,
        dataType: "date",
        format: "MM/dd/yyyy",
        allowFiltering: true,
        allowSorting: true,
        allowHeaderFiltering: false,
      },
      {
        dataField: "statementToDate",
        caption: "Service To Date",
        dataType: "date",
        format: "MM/dd/yyyy",
        allowFiltering: true,
        allowSorting: true,
        allowHeaderFiltering: false,
        width: 120,
      },
      {
        dataField: "participantName",
        caption: "Patient Name",
        allowFiltering: true,
        allowSorting: true,
        allowHeaderFiltering: false,
      },
      {
        dataField: "providerName",
        caption: "Provider",
        allowFiltering: true,
        allowSorting: true,
        allowHeaderFiltering: false,
      },
      {
        dataField: "facilityName",
        caption: "Facility",
        allowFiltering: true,
        allowSorting: true,
        allowHeaderFiltering: false,
      },
      {
        dataField: "payerRemitNo",
        caption: "Payer Remit #",
      },
    ],
    mdaeDataUpdaterConfig: [
      {
        actionType: SET_CASE_LIST_GRID_SELECTED_ROW_KEYS,
        reducerName: "caseSearchReducer",
        widgetOptionBindings: {
          selectedRowKeys: "caseListGridSelectedRowKeys",
        },
      },
    ],
  };
  return caseSearchListDataGridOptions;
};
export const RadioSelectionCellComponent = (params) => {
  const selectedCaseDetailIdsToAddToAudit: number[] = useSelector(
    (state) => state["caseSearchReducer"].selectedCaseDetailIdsToAddToAudit
  );
  const items = useMemo(() => [{ ID: true }], []);
  return (
    <div className="radio-selection-button-cell">
      <MdaeRadioGroup
        id={""}
        value={selectedCaseDetailIdsToAddToAudit.includes(params.data.data.caseDetailId)}
        items={items}
        valueExpr="ID"
      />
    </div>
  );
};

export default CaseSearchListPopup;